import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { SpeechToTextService, TemporaryKey } from '@isophi/mng-api';
import { firstValueFrom } from 'rxjs';

export type DictationState = 'idle' | 'starting' | 'listening' | 'stopping';
export type DictationError = 'micDenied' | 'notConfigured' | 'unavailable' | 'connectionLost';

export interface DictationCallbacks {
  /** Newly finalised text (Soniox tokens keep their leading spaces). */
  finalText(text: string): void;
  /** The current not-yet-final text; replaces the previous one. */
  partialText(text: string): void;
  stateChange(state: DictationState): void;
  error(error: DictationError): void;
}

interface SonioxToken {
  text: string;
  is_final?: boolean;
}

interface SonioxResponse {
  tokens?: SonioxToken[];
  finished?: boolean;
  error_code?: number;
  error_message?: string;
}

// Endpoint detection marks the end of an utterance with this token; it is not text.
const END_TOKEN = '<end>';
const CHUNK_MS = 250;
const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm', 'audio/mp4'];

/** One dictation: microphone → MediaRecorder chunks → Soniox real-time WebSocket (POR-001 §5). */
export class DictationSession {
  private stopping = false;
  private closed = false;

  constructor(
    private readonly stream: MediaStream,
    private readonly socket: WebSocket,
    private readonly recorder: MediaRecorder,
    private readonly callbacks: DictationCallbacks,
    private readonly onClose: (session: DictationSession) => void
  ) {
    socket.onmessage = (event) => this.handleMessage(event.data);
    socket.onerror = () => this.fail('connectionLost');
    socket.onclose = () => (this.stopping ? this.close() : this.fail('connectionLost'));
    recorder.ondataavailable = (event) => {
      if (event.data.size && socket.readyState === WebSocket.OPEN) socket.send(event.data);
    };
    // The last chunk arrives before `stop`; an empty frame then asks Soniox to finalise and finish.
    recorder.onstop = () => {
      if (socket.readyState === WebSocket.OPEN) socket.send('');
    };
  }

  /** Stops recording; the remaining text still arrives before the session closes. */
  stop(): void {
    if (this.stopping || this.closed) return;
    this.stopping = true;
    this.callbacks.stateChange('stopping');
    this.releaseMicrophone();
    if (this.recorder.state !== 'inactive') this.recorder.stop();
    else if (this.socket.readyState === WebSocket.OPEN) this.socket.send('');
  }

  /** Ends at once without waiting for the rest of the text (page left). */
  cancel(): void {
    this.stopping = true;
    if (this.recorder.state !== 'inactive') this.recorder.stop();
    this.socket.close();
    this.close();
  }

  private handleMessage(data: string): void {
    let response: SonioxResponse;
    try {
      response = JSON.parse(data);
    } catch {
      return;
    }
    if (response.error_code) {
      this.fail(response.error_code === 401 || response.error_code === 403 ? 'unavailable' : 'connectionLost');
      return;
    }
    let finalText = '';
    let partialText = '';
    for (const token of response.tokens ?? []) {
      if (!token.text || token.text === END_TOKEN) continue;
      if (token.is_final) finalText += token.text;
      else partialText += token.text;
    }
    if (finalText) this.callbacks.finalText(finalText);
    this.callbacks.partialText(partialText);
    if (response.finished) this.close();
  }

  private fail(error: DictationError): void {
    if (this.closed) return;
    this.callbacks.error(error);
    this.cancel();
  }

  private close(): void {
    if (this.closed) return;
    this.closed = true;
    this.releaseMicrophone();
    if (this.socket.readyState === WebSocket.OPEN) this.socket.close();
    this.callbacks.partialText('');
    this.callbacks.stateChange('idle');
    this.onClose(this);
  }

  private releaseMicrophone(): void {
    this.stream.getTracks().forEach((track) => track.stop());
  }
}

/** Hlasové diktování through Soniox; one dictation runs at a time, a new one stops the previous. */
@Injectable({ providedIn: 'root' })
export class DictationService {
  private readonly speechToText = inject(SpeechToTextService);
  private active: DictationSession | null = null;
  /** False once the backend said it has no Soniox key: every dictation button hides (a signal, for OnPush views). */
  readonly configured = signal(true);

  get supported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== 'undefined' &&
      typeof WebSocket !== 'undefined'
    );
  }

  async start(callbacks: DictationCallbacks): Promise<DictationSession | null> {
    this.active?.stop();
    callbacks.stateChange('starting');

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch {
      return this.abort(callbacks, 'micDenied');
    }

    let key: TemporaryKey;
    try {
      key = await firstValueFrom(this.speechToText.speechToTextTemporaryKeyCreate());
    } catch (error) {
      stream.getTracks().forEach((track) => track.stop());
      const notConfigured = (error as HttpErrorResponse)?.status === 503;
      if (notConfigured) this.configured.set(false);
      return this.abort(callbacks, notConfigured ? 'notConfigured' : 'unavailable');
    }

    const mimeType = MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    const socket = new WebSocket(key.websocketUrl);
    const session = new DictationSession(stream, socket, recorder, callbacks, (closed) => {
      if (this.active === closed) this.active = null;
    });
    socket.onopen = () => {
      socket.send(
        JSON.stringify({
          api_key: key.apiKey,
          model: key.model,
          audio_format: 'auto',
          language_hints: ['cs'],
          enable_endpoint_detection: true,
          context: {
            general: [
              { key: 'domain', value: 'mateřská škola' },
              { key: 'topic', value: 'zápis vzdělávacích činností a pozorování dětí' },
            ],
          },
        })
      );
      recorder.start(CHUNK_MS);
      callbacks.stateChange('listening');
    };
    this.active = session;
    return session;
  }

  private abort(callbacks: DictationCallbacks, error: DictationError): null {
    callbacks.error(error);
    callbacks.stateChange('idle');
    return null;
  }
}
